import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, Eye, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { RetailerRequest } from '../../types';
import { getRetailerRequests } from '../../services/retailerService';
import RequestDetailModal from './RequestDetailModal';

interface RetailerRequestsTabProps {
  onUpdate: () => void;
}

const RetailerRequestsTab: React.FC<RetailerRequestsTabProps> = ({ onUpdate }) => {
  const [requests, setRequests] = useState<RetailerRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<RetailerRequest | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getRetailerRequests();
      setRequests(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestUpdate = () => {
    loadRequests();
    onUpdate();
  };

  const filteredRequests = filter === 'all'
    ? requests
    : requests.filter(r => r.status === filter);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] tracking-micro uppercase bg-brass/10 text-brass border border-brass/30">
            <Clock className="w-3 h-3" strokeWidth={1.5} />
            Pending
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] tracking-micro uppercase bg-olive/15 text-olive border border-olive/40">
            <CheckCircle className="w-3 h-3" strokeWidth={1.5} />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] tracking-micro uppercase bg-ember-soft/25 text-ember border border-ember/30">
            <XCircle className="w-3 h-3" strokeWidth={1.5} />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-ember" strokeWidth={1.5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-ember-soft/25 border-l-2 border-ember p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
        <div>
          <p className="text-ember-deep font-medium text-sm">Error loading requests</p>
          <p className="text-ember text-sm">{error}</p>
          <button
            onClick={loadRequests}
            className="mt-2 text-ember-deep underline text-sm hover:text-ember transition-colors duration-150"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-3">— Applications</p>
          <h2 className="font-editorial italic font-light text-3xl text-ink-950 tracking-tight">
            Retailer applications.
          </h2>
          <p className="text-ink-700 text-sm mt-1">Review and manage new retailer requests.</p>
        </div>
        <button
          onClick={loadRequests}
          className="inline-flex items-center gap-2 px-4 py-2 text-ink-700 hover:text-ink-950 hover:bg-paper-bright border border-ink-200 hover:border-ink-300 text-sm font-medium transition-colors duration-150"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {(['pending', 'approved', 'rejected', 'all'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 text-sm font-medium transition-colors duration-150 border ${
              filter === status
                ? 'bg-ember text-paper-warm border-ember'
                : 'bg-paper-bright text-ink-700 border-ink-200 hover:border-ink-300 hover:bg-paper'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status !== 'all' && (
              <span className="ml-1.5 font-mono text-xs opacity-75">
                ({requests.filter(r => r.status === status).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Table */}
      {filteredRequests.length === 0 ? (
        <div className="bg-paper-bright border border-ink-200 p-8 text-center">
          <p className="text-ink-500 text-sm">No {filter === 'all' ? '' : filter} requests found.</p>
        </div>
      ) : (
        <div className="bg-paper-bright border border-ink-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-paper border-b border-ink-200">
              <tr>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Business</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Contact</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Location</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Submitted</th>
                <th className="px-4 py-3 text-left font-mono text-[10px] tracking-micro uppercase text-ink-500">Status</th>
                <th className="px-4 py-3 text-right font-mono text-[10px] tracking-micro uppercase text-ink-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200">
              {filteredRequests.map((request) => (
                <tr key={request.id} className="hover:bg-paper transition-colors duration-150">
                  <td className="px-4 py-4">
                    <div>
                      <p className="font-medium text-ink-950 text-sm">{request.businessName}</p>
                      <p className="text-xs text-ink-500 mt-0.5">{request.businessType}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div>
                      <p className="text-ink-900 text-sm">{request.contactName}</p>
                      <p className="text-xs text-ink-500 mt-0.5">{request.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-ink-700 text-sm">
                    {request.address.city}, {request.address.province}
                  </td>
                  <td className="px-4 py-4 text-ink-700 text-sm">
                    {request.submittedAt.toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4">
                    {getStatusBadge(request.status)}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button
                      onClick={() => setSelectedRequest(request)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-ember hover:text-ember-deep hover:bg-ember-soft/25 transition-colors duration-150"
                    >
                      <Eye className="w-4 h-4" strokeWidth={1.5} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onUpdate={handleRequestUpdate}
        />
      )}
    </div>
  );
};

export default RetailerRequestsTab;
